<template>
  <LocaleProviderZh>
    <a-table
      ref="table"
      class="lin-table"
      v-bind="$props"
      v-on="$listeners"
      @change="handleChange"
      :data-source="table.data"
      :columns="table.columns"
      :row-selection="rowSelectionProp"
      :pagination="pagination"
      :loading="table.loading"
    >
      <template
        v-for="item in slotScopeNames"
        :slot="item"
        slot-scope="text, record, index"
      >
        <slot :name="item" :record="record" :text="text" :index="index"></slot>
      </template>

      <template :slot="item" v-for="item in slotNames">
        <slot :name="item"></slot>
      </template>
      <template slot="footer">
        <slot name="footer"> </slot>
      </template> </a-table
  ></LocaleProviderZh>
</template>

<script>
import { Table } from "ant-design-vue";
import isTextEllipsis from '@/utils/isTextEllipsis'
// eslint-disable-next-line no-unused-vars
const { dataSource, columns, pagination, ...tableRestProps } = Table.props;
export default {
  name: "LinTable",
  props: {
    ...tableRestProps,
    scroll: {
      type: Object,
      default: () => ({
        x: "max-content",
        y: "max-content",
        scrollToFirstRowOnChange: true,
      }),
    },
    table: {
      type: Object,
      default: () => ({
        data: [], // 数据源
        columns: [
          // {
          // ...原组件配置
          // placeholder: '-', 数据为 字符串 时的占位符
          // ellipsis: boolean, // 内部文案是否换行
          // 自定义显示的文案，比customRender轻便一点的处理，如果是返回数组，则分行显示，在显示员工信息时很有用
          // customText: (text, record, index) => string 或是 string[],
          // contentClassName: string, // 自定义样式名
          // onClick: (text, record, index) => void, // 点击事件
          // }
        ], // 列配置
        pagination: {
          current: 1,
          pageSize: 10,
          total: 0,
        }, // 分页配置 https://www.antdv.com/components/pagination-cn/#API

        // hasIndex: false, // 是否加入序号列
        // autoWidth: false, // 是否自动根据表头文字长度计算列宽度
        // selection: [], // 是否有选择框列
        // selectionType: '', // 选择框类型，默认为多选，可填 radio 切为单选
        // defaultPlaceholder: '' // 默认所有列的placeholder
        // useTooltip: false, // 是否使用tooltip显示超出部分内容
      }),
    },
    getData: {
      type: Function,
      default: () => {},
    },
    // 是否自动计算表格高度
    fitHeight: {
      type: Boolean,
      default: true,
    },
    // 自动计算高度时表格距离底部的像素值
    fitHeightBottom: {
      type: Number,
      default: 15 + 6,
    },
    columnEllipsis: {
      type: Boolean,
      default: true,
    },
    handleSelectProps: {
      type: Function,
      default: () => ({
        props: {
          disabled: false, // Column configuration not to be checked
        },
      }),
    },
    // 在选择行前触发的校验
    handleBeforeSelect: {
      type: Function, // () => Promise<boolean> | () => boolean
    },
    // 首次加载后是否获取数据
    isFirstGetData: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      selectedRowKeys: [],
      currentScroll: {},
      oldColumnLength: this.table?.columns?.length || 0
    };
  },
  watch: {
    "table.columns": {
      handler(columns) {
        if (this.fitHeight) {
          this.parseTableHeight();
        }
        if (columns?.length === this.oldColumnLength && JSON.stringify(columns) === JSON.stringify(this.table.columns)) {
          return;
        }
        this.parseColumns(columns);
      },
      immediate: true,
    },
    "table.selection": {
      handler(selection) {
        if (!this.rowKey) {
          console.warn("必须添加rowKey属性");
        }
        if (selection) {
          this.selectedRowKeys = selection.map((_) => _[this.rowKey]);
        }
      },
      immediate: true,
    },
  },
  computed: {
    slotScopeNames() {
      return this.table.columns.reduce((pre, cur) => {
        if (cur.scopedSlots) {
          pre.push(...Object.values(cur.scopedSlots));
        }

        return pre;
      }, []);
    },

    slotNames() {
      return this.table.columns.reduce((pre, cur) => {
        if (cur.slots) {
          pre.push(...Object.values(cur.slots));
        }

        return pre;
      }, []);
    },

    pagination() {
      if (this.table.pagination) {
        return {
          showTotal: (total) => `共${total}条记录`,
          showSizeChanger: true,
          ...this.table.pagination,
        };
      } else {
        return false;
      }
    },
    rowSelectionProp() {
      if (this.table.selection) {
        return {
          ...(this.table?.selectionProps || {}),
          selectedRowKeys: this.selectedRowKeys,
          onChange: this.handleSelectOnChange,
          onSelect: this.handleSelectOnSelect,
          onSelectAll: this.handleSelectOnSelectAll,
          getCheckboxProps: this.handleSelectProps,
          type: this.table.selectionType || "checkbox",
        };
      } else {
        return null;
      }
    },
  },
  created() {
    if (
      this.getData &&
      typeof this.getData === "function" &&
      this.isFirstGetData
    ) {
      this.getData();
    }
    this.parseColumns(this.table.columns);
  },
  mounted() {
    if (this.fitHeight) {
      this.parseTableHeight();
    }
  },
  methods: {
    /**
     * 处理columns
     * @param columns
     */
    parseColumns(columns) {
      let {
        hasIndex,
        pagination,
        autoWidth,
        defaultPlaceholder,
        useTooltip,
      } = this.table;
      if (hasIndex) {
        columns = [
          {
            title: "序号",
            dataIndex: "_index",
            width: 60,
            customRender: (text, record, index) => {
              if (pagination) {
                const { current, pageSize } = pagination;
                return <span>{index + 1 + (current - 1) * pageSize}</span>;
              }
              return <span>{index + 1}</span>;
            },
            fixed: columns[0]?.fixed,
          },
          ...columns,
        ];
      }

      if (this.columnEllipsis) {
        let handle = (col) => {
          let {
            width: colWidth,
            ellipsis,
            customText,
            contentClassName = "",
            onClick,
          } = col;
          if (autoWidth && !col.width) {
            colWidth = (col.title.length + 1) * 14 + 32;
          }

          if (typeof ellipsis !== "boolean") {
            ellipsis = true;
          }

          return {
            customRender: col.scopedSlots
              ? undefined
              : (text, record, index) => {
                  if (customText) {
                    // 自定义输出文案
                    text = customText(text, record, index);
                  } else if (col.placeholder || defaultPlaceholder) {
                    text =
                      !text && text !== 0
                        ? col.placeholder || defaultPlaceholder || ""
                        : text;
                  }

                  let className = ellipsis ? 'text-ellipsis-1' : ''
                  if (contentClassName) {
                    className += ` ${contentClassName}`
                  }

                  return (
                    <div
                      v-title={this.getTitleDirective(text)}
                      title={useTooltip ? '' : text}
                      class={className}
                      onClick={() => onClick && onClick(text, record, index)}
                    >
                      {Array.isArray(text)
                        ? text.map((itemText) => {
                            return (
                              <div
                                class={className}
                                title={useTooltip ? '' : itemText}
                                 v-title={this.getTitleDirective(itemText)}
                              >
                                {itemText}
                              </div>
                            );
                          })
                        : text}
                    </div>
                  );
                },
            ...col,
            width: colWidth,
            ellipsis,
          };
        };

        this.table.columns = columns.map((col) => {
          if (col.children) {
            col.children = col.children.map((item) => handle(item));
          }

          return handle(col);
        });
      }

      this.oldColumnLength = this.table.columns.length;
    },
    getTitleDirective(text) {
      return {
        content: text,
        onShow: (ins) => {
          let el = ins.reference
          return !!this.table.useTooltip && isTextEllipsis(el)
        },
      };
    },
    /**
     * 处理table组件的高度
     */
    parseTableHeight() {
      setTimeout(() => {
        const tableRef = this.$refs["table"].$el;
        const table = tableRef.querySelector(".ant-table");
        const top = tableRef.getBoundingClientRect().top;
        const headHeight = tableRef.querySelector(".ant-table-thead")
          .clientHeight;
        const paginationHeight = 48;
        const bottomGap = this.fitHeightBottom; // 底部距离
        const y =
          top +
          headHeight +
          bottomGap +
          (this.pagination ? paginationHeight : 0);

        table.style.height = `calc(100vh - ${top +
          bottomGap +
          (this.pagination ? paginationHeight : 0)}px)`;
        this.scroll.y = `calc(100vh - ${y}px)`;
      }, 20);
    },
    /**
     * 处理table页码变化
     * @param pagination
     */
    handleChange(pagination, filters) {
      if (typeof this.getData === "function") {
        if (this.table?.pagination) {
          this.table.pagination = Object.assign(
            this.table.pagination,
            pagination
          );
        }
        if (this.table?.filters) {
          this.table.filters = Object.assign(this.table.filters, filters);
        }
        this.getData();
      } else {
        console.warn("你可能需要将getData函数传给LinTable组件");
      }
    },
    // eslint-disable-next-line no-unused-vars
    handleSelectOnChange(selectedRowKeys, selectedRows) {
      this.$emit("selection-change", selectedRowKeys, selectedRows);
    },
    async handleSelectOnSelect(record, selected) {
      // 检验选择的数据
      if (this.handleBeforeSelect) {
        let flag = await this.handleBeforeSelect(record, selected);

        if (!flag) return;
      }

      if (this.table.selectionType === "radio") {
        // 单选
        this.table.selection = [record];
      } else {
        // 多选
        const index = this.selectedRowKeys.indexOf(record[this.rowKey]);
        if (selected) {
          if (index === -1) {
            this.selectedRowKeys.push(record[this.rowKey]);
            this.table.selection.push(record);
          }
        } else {
          if (index > -1) {
            this.selectedRowKeys.splice(index, 1);
            this.table.selection.splice(index, 1);
          }
        }
      }
    },
    handleSelectOnSelectAll(selected, selectedRows, changedRows) {
      for (let record of changedRows) {
        const index = this.selectedRowKeys.indexOf(record[this.rowKey]);
        if (selected) {
          if (index === -1) {
            this.selectedRowKeys.push(record[this.rowKey]);
            this.table.selection.push(record);
          }
        } else {
          if (index > -1) {
            this.selectedRowKeys.splice(index, 1);
            this.table.selection.splice(index, 1);
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.lin-table {
  /deep/ .ant-table-pagination.ant-pagination {
    margin-bottom: 0;
  }

  /deep/ .ant-table .ant-btn-link {
    user-select: auto;
  }

  .text-overflow {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
}
</style>
